import { FREE_SHIPPING_SPEEDS, HIGHLIGHTED_SHIPPING_OPTION_MAP } from 'constants/shipping';
import {
  ZAPPOS_FREE_HOLIDAY_SHIPPING,
  ZAPPOS_LEGACY_VIP,
  ZAPPOS_LWA_PLUS_PRIME,
  ZAPPOS_LWA_PRIME_NO_VIP_AT_CHECKOUT,
  ZAPPOS_MOBILE_PROMO,
  ZAPPOS_NEW_VIP,
  ZAPPOS_PLAIN,
  ZAPPOS_PLAIN_NO_VIP_AT_CHECKOUT,
  ZAPPOS_REWARDS_ELITE,
  ZAPPOS_REWARDS_GOLD,
  ZAPPOS_REWARDS_PLATINUM,
  ZAPPOS_REWARDS_SILVER,
  ZAPPOS_REWARDS_SILVER_AS_GOLD,
  ZAPPOS_VIP_AT_CHECKOUT
} from 'constants/shippingBenefitReasons';

// Shipping option labels (copied from Kratos dictionary)
const SHIPPING_OPTION_LABELS = {
  vipPerk: 'As a VIP member, all shipping options are FREE! Thanks for being a VIP!',
  vipNonPerk: 'VIP Shipping',
  primePerk: 'As a Prime member, you have more FREE shipping options to choose from.',
  holidayPerk: 'FREE limited-time holiday shipping!',
  basePerk: 'FREE Zappos Standard Shipping',
  baseNonPerk: 'Zappos Standard Shipping',
  baseFreeShipping: 'Free Shipping',
  basePaidShipping: 'Paid Shipping'
};

export const formatShippingOptionDescription = (shippingBenefitReason: number, shipmentSpeed: string, price: number): string => {
  const isVipAtCheckout = isVipAtCheckoutSelected(shippingBenefitReason, shipmentSpeed);
  const isVipPerk = isOneDayVipShippingPerk(shippingBenefitReason);
  const isPrimePerk = isTwoDayPrimeShippingPerk(shippingBenefitReason);
  const isHolidayPerk = isFreeHolidayShipping(shippingBenefitReason);
  const isBasePerk = isBaseZapposShipping(shippingBenefitReason);
  const isZappos = isZapposShipping(shippingBenefitReason);

  if (isVipAtCheckout && !isFreeShippingSpeed(shipmentSpeed)) {
    return SHIPPING_OPTION_LABELS.vipPerk;
  } else if (isVipPerk && !!HIGHLIGHTED_SHIPPING_OPTION_MAP.vip[shipmentSpeed]) {
    return SHIPPING_OPTION_LABELS.vipNonPerk;
  } else if (isVipPerk && shipmentSpeed !== 'next-wow') {
    return SHIPPING_OPTION_LABELS.vipNonPerk;
  } else if (isPrimePerk && !!HIGHLIGHTED_SHIPPING_OPTION_MAP.prime[shipmentSpeed]) {
    return SHIPPING_OPTION_LABELS.primePerk;
  } else if (isHolidayPerk && shipmentSpeed !== 'next-wow') {
    return SHIPPING_OPTION_LABELS.holidayPerk;
  } else if (isBasePerk && isFreeShippingSpeed(shipmentSpeed)) {
    return SHIPPING_OPTION_LABELS.basePerk;
  } else if (isZappos && isFreeShippingSpeed(shipmentSpeed)) {
    return SHIPPING_OPTION_LABELS.baseNonPerk;
  } else if (price === 0) {
    return SHIPPING_OPTION_LABELS.baseFreeShipping;
  } else {
    return SHIPPING_OPTION_LABELS.basePaidShipping;
  }
};

export const isVipAtCheckoutSelected = (shippingBenefitReason: number, shipmentSpeed: string): boolean =>
  !!shipmentSpeed && shippingBenefitReason === ZAPPOS_VIP_AT_CHECKOUT;

export const isOneDayVipShippingPerk = (shippingBenefitReason: number): boolean =>
  [
    ZAPPOS_REWARDS_GOLD,
    ZAPPOS_LEGACY_VIP,
    ZAPPOS_REWARDS_PLATINUM,
    ZAPPOS_REWARDS_ELITE,
    ZAPPOS_REWARDS_SILVER_AS_GOLD,
    ZAPPOS_VIP_AT_CHECKOUT,
    ZAPPOS_NEW_VIP
  ].includes(shippingBenefitReason);

export const isTwoDayPrimeShippingPerk = (shippingBenefitReason: number): boolean =>
  shippingBenefitReason === ZAPPOS_LWA_PLUS_PRIME || shippingBenefitReason === ZAPPOS_LWA_PRIME_NO_VIP_AT_CHECKOUT;

export const isFreeHolidayShipping = (shippingBenefitReason: number): boolean => shippingBenefitReason === ZAPPOS_FREE_HOLIDAY_SHIPPING;

export const isBaseZapposShipping = (shippingBenefitReason: number): boolean =>
  [ZAPPOS_PLAIN, ZAPPOS_PLAIN_NO_VIP_AT_CHECKOUT, ZAPPOS_MOBILE_PROMO, ZAPPOS_REWARDS_SILVER].includes(shippingBenefitReason);

export const isZapposShipping = (shippingBenefitReason: number): boolean =>
  [
    ZAPPOS_PLAIN,
    ZAPPOS_PLAIN_NO_VIP_AT_CHECKOUT,
    ZAPPOS_MOBILE_PROMO,
    ZAPPOS_REWARDS_SILVER,
    ZAPPOS_FREE_HOLIDAY_SHIPPING,
    ZAPPOS_LWA_PLUS_PRIME,
    ZAPPOS_LWA_PRIME_NO_VIP_AT_CHECKOUT,
    ZAPPOS_REWARDS_GOLD,
    ZAPPOS_LEGACY_VIP,
    ZAPPOS_REWARDS_PLATINUM,
    ZAPPOS_REWARDS_ELITE,
    ZAPPOS_REWARDS_SILVER_AS_GOLD,
    ZAPPOS_VIP_AT_CHECKOUT,
    ZAPPOS_NEW_VIP
  ].includes(shippingBenefitReason);

export const isFreeShippingSpeed = (shipmentSpeed: string): boolean => FREE_SHIPPING_SPEEDS.includes(shipmentSpeed);
